import React, { useState, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import BCALogo from '../assets/images/BCA logo.jpg';
import DBDBottle from '../assets/images/DBD-bottle-v1.png';
import html2canvas from 'html2canvas';
import './ArcheryScorecard.css';

// Create an End interface
const End = ({ endNumber, arrows, onChange }) => {
  return (
    <div className="end-block">
      <div className="end-title">End {endNumber}</div>
      <div className="arrow-inputs">
        {arrows.map((arrow, index) => (
          <input
            key={index}
            type="number"
            min="0"
            max="10"
            value={arrow}
            onChange={(e) => onChange(endNumber - 1, index, e.target.value)}
            className="arrow-input"
          />
        ))}
      </div>
      <div className="end-total">Total: {arrows.reduce((sum, val) => sum + (parseInt(val) || 0), 0)}</div>
    </div>
  );
};

// Practice Round component
const PracticeRound = ({ distance, arrows, onChange }) => {
  return (
    <div className="practice-round-block">
      <div className="practice-title">Practice Round</div>
      <div className="arrow-inputs">
        {arrows.map((arrow, index) => (
          <input
            key={index}
            type="number"
            min="0"
            max="10"
            value={arrow}
            onChange={(e) => onChange(distance, index, e.target.value)}
            className="arrow-input practice-input"
          />
        ))}
      </div>
      <div className="end-total">Total: {arrows.reduce((sum, val) => sum + (parseInt(val) || 0), 0)}</div>
    </div>
  );
};

// Target visualization component
const TargetVisualization = ({ ends, practiceRounds, distance }) => {
  const ringColors = [
    '#FFFFFF', // 1-2 (White)
    '#FFFFFF',
    '#000000', // 3-4 (Black)
    '#000000',
    '#62B5E5', // 5-6 (Blue)
    '#62B5E5',
    '#D8262C', // 7-8 (Red)
    '#D8262C',
    '#F7D94C', // 9-10 (Yellow)
    '#F7D94C'
  ];

  // Count arrows for the specific scoring ring
  const countArrows = (scoreValue) => {
    let relevantEnds;
    if (distance === '10m') {
      relevantEnds = ends.slice(0, 3);
    } else if (distance === '15m') {
      relevantEnds = ends.slice(3, 6);
    } else {
      relevantEnds = ends;
    }

    // Only count non-zero values that match the score
    return relevantEnds.reduce((count, end) => 
      count + end.arrows.filter(arrow => arrow === scoreValue && arrow !== 0).length, 0);
  };

  return (
    <svg viewBox="0 0 200 200" className="target-svg">
      {/* Background circle */}
      <circle cx="100" cy="100" r="95" fill="white" stroke="black" />
      
      {/* Create scoring rings from outside in */}
      {ringColors.map((color, i) => {
        const radius = 95 - (i * 9.5);
        return (
          <circle
            key={i}
            cx="100"
            cy="100"
            r={radius}
            fill={color}
            stroke="black"
            strokeWidth="0.25"
          />
        );
      })}

      {/* Show hit counts */}
      {[...Array(10)].map((_, i) => {
        const score = i + 1;
        const count = countArrows(score);
        if (count > 0) {
          const outerRadius = 95 - ((score - 1) * 9.5);
          const innerRadius = 95 - (score * 9.5);
          const centerRadius = (outerRadius + innerRadius) / 2;
          
          return (
            <text
              key={score}
              x="100"
              y={100 - centerRadius + 4}
              textAnchor="middle"
              fontSize="10"
              fontWeight="bold"
              fill={score >= 9 ? 'black' : 
                    score >= 7 ? 'white' : 
                    score >= 5 ? 'white' : 
                    score >= 3 ? 'white' : 
                    'black'}
            >
              {count}
            </text>
          );
        }
        return null;
      })}

      {/* Distance label */}
      <text
        x="30"
        y="20"
        fontSize="10"
        fontWeight="bold"
      >
        {distance}
      </text>
    </svg>
  );
};

// Main component
const ArcheryScorecard = () => {
  const scorecardRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    school: '',
    practiceRounds: {
      tenMeter: { arrows: Array(5).fill('') },
      fifteenMeter: { arrows: Array(5).fill('') }
    },
    ends: Array(6).fill().map(() => ({ arrows: Array(5).fill('') }))
  });

  const [scores, setScores] = useState({
    totalScore: 0,
    tenMeterScore: 0,
    fifteenMeterScore: 0,
    dropPercentage: '0%',
    endAverages: [0, 0, 0, 0, 0, 0],
    chartData: []
  });

  const handleSaveAsImage = () => {
    console.log('Save button clicked');
    
    if (scorecardRef.current) {
      console.log('Scorecard ref found, starting capture...');
      
      // Find the name and school inputs more reliably
      const textInputs = scorecardRef.current.querySelectorAll('input[type="text"]');
      const nameInput = textInputs[0]; // First text input should be name
      const schoolInput = textInputs[1]; // Second text input should be school
      
      // Store original elements and their parents
      const originalElements = [];
      
      // Replace name input with text div
      if (nameInput && nameInput.parentNode) {
        const nameDiv = document.createElement('div');
        nameDiv.textContent = formData.name || '';
        nameDiv.className = 'capture-text-replacement';
        
        originalElements.push({
          original: nameInput,
          replacement: nameDiv,
          parent: nameInput.parentNode
        });
        
        nameInput.parentNode.replaceChild(nameDiv, nameInput);
      }
      
      // Replace school input with text div
      if (schoolInput && schoolInput.parentNode) {
        const schoolDiv = document.createElement('div');
        schoolDiv.textContent = formData.school || '';
        schoolDiv.className = 'capture-text-replacement';
        
        originalElements.push({
          original: schoolInput,
          replacement: schoolDiv,
          parent: schoolInput.parentNode
        });
        
        schoolInput.parentNode.replaceChild(schoolDiv, schoolInput);
      }
      
      // Force all remaining inputs to show their values as attributes
      const remainingInputs = scorecardRef.current.querySelectorAll('input');
      remainingInputs.forEach(input => {
        if (input.value) {
          input.setAttribute('value', input.value);
        }
      });
      
      // Capture the image
      html2canvas(scorecardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true
      }).then(canvas => {
        console.log('Canvas created successfully');
        
        // Restore original inputs
        originalElements.forEach(({ original, replacement, parent }) => {
          parent.replaceChild(original, replacement);
        });
        
        const link = document.createElement('a');
        link.download = `bat-city-scorecard-${formData.name || 'untitled'}.png`;
        link.href = canvas.toDataURL('image/png');
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('Download should have started');
        
      }).catch(error => {
        console.error('html2canvas error:', error);
        
        // Restore original inputs even if there's an error
        originalElements.forEach(({ original, replacement, parent }) => {
          parent.replaceChild(original, replacement);
        });
        
        alert('Error creating image: ' + error.message);
      });
    } else {
      console.error('Scorecard ref not found');
    }
  };

  // Calculate scores when arrow values change
  const calculateScores = (ends, practiceRounds = formData.practiceRounds) => {
    const tenMeterEnds = ends.slice(0, 3);
    const fifteenMeterEnds = ends.slice(3, 6);
    
    const tenMeterScore = tenMeterEnds.reduce((sum, end) => 
      sum + end.arrows.reduce((endSum, arrow) => endSum + (parseInt(arrow) || 0), 0), 0);
    
    const fifteenMeterScore = fifteenMeterEnds.reduce((sum, end) => 
      sum + end.arrows.reduce((endSum, arrow) => endSum + (parseInt(arrow) || 0), 0), 0);
    
    const totalScore = tenMeterScore + fifteenMeterScore;
    const dropPercentage = tenMeterScore > 0 
      ? ((tenMeterScore - fifteenMeterScore) / tenMeterScore * 100).toFixed(0) + '%'
      : '0%';
    
    const endAverages = ends.map(end => 
      end.arrows.reduce((sum, arrow) => sum + (parseInt(arrow) || 0), 0));

    // Calculate practice round scores for the chart
    const tenMeterPractice = practiceRounds.tenMeter.arrows.reduce(
      (sum, arrow) => sum + (parseInt(arrow) || 0), 0);
    const fifteenMeterPractice = practiceRounds.fifteenMeter.arrows.reduce(
      (sum, arrow) => sum + (parseInt(arrow) || 0), 0);

    // Create chart data that includes practice rounds with simplified labels for end 2 and 5
    const chartData = [
      { name: 'PR', score: tenMeterPractice },
      { name: 'End 1', score: endAverages[0] },
      { name: '2', score: endAverages[1] },  // Just "2" instead of "End 2"
      { name: 'End 3', score: endAverages[2] },
      { name: 'PR', score: fifteenMeterPractice },
      { name: 'End 4', score: endAverages[3] },
      { name: '5', score: endAverages[4] },  // Just "5" instead of "End 5" 
      { name: 'End 6', score: endAverages[5] }
    ];

    setScores({
      totalScore,
      tenMeterScore,
      fifteenMeterScore,
      dropPercentage,
      endAverages,
      chartData
    });
  };

  // Handle changes to arrow scores
  const handleArrowChange = (endIndex, arrowIndex, value) => {
    // Allow explicit zeros by treating empty strings and "0" differently
    const newValue = value === '' ? '' : Math.max(0, Math.min(10, parseInt(value) || 0));
    const newEnds = [...formData.ends];
    
    // Make sure the end exists
    if (!newEnds[endIndex]) {
      newEnds[endIndex] = { arrows: Array(5).fill('') };
    }
    
    // Create a new arrows array
    const newArrows = [...newEnds[endIndex].arrows];
    newArrows[arrowIndex] = newValue;
    
    // Update the end with the new arrows
    newEnds[endIndex] = { ...newEnds[endIndex], arrows: newArrows };
    
    // Update form data and recalculate scores
    setFormData(prev => ({ ...prev, ends: newEnds }));
    calculateScores(newEnds);
  };

  // Handle changes to practice round scores
  const handlePracticeArrowChange = (distance, arrowIndex, value) => {
    const newValue = value === '' ? '' : Math.max(0, Math.min(10, parseInt(value) || 0));
    const newPracticeRounds = { ...formData.practiceRounds };
    
    if (distance === '10m') {
      const newArrows = [...newPracticeRounds.tenMeter.arrows];
      newArrows[arrowIndex] = newValue;
      newPracticeRounds.tenMeter = { arrows: newArrows };
    } else {
      const newArrows = [...newPracticeRounds.fifteenMeter.arrows];
      newArrows[arrowIndex] = newValue;
      newPracticeRounds.fifteenMeter = { arrows: newArrows };
    }
    
    setFormData(prev => ({ ...prev, practiceRounds: newPracticeRounds }));
    calculateScores(formData.ends, newPracticeRounds);
  };

  return (
    <div className="archery-scorecard" ref={scorecardRef}>
      {/* Header */}
      <div className="scorecard-header">
        <img 
          src={BCALogo}
          alt="Bat City Archery Logo"
          className="scorecard-logo"
        />
        <h1 className="scorecard-title">Bat City Archery Online Performance Tracker</h1>
        <button className="save-button" onClick={handleSaveAsImage}>Save as Image</button>
      </div>

      <div className="scorecard-content">
        {/* Name and School */}
        <div className="input-row">
          <div className="input-group">
            <label>Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div className="input-group">
            <label>School</label>
            <input
              type="text"
              value={formData.school}
              onChange={(e) => setFormData(prev => ({ ...prev, school: e.target.value }))}
            />
          </div>
        </div>

        {/* Target Visualization */}
        <div className="section">
          <h2 className="section-title">Target Visualization</h2>
          <div className="targets-container">
            <div className="target">
              <TargetVisualization 
                ends={formData.ends} 
                practiceRounds={formData.practiceRounds}
                distance="10m" 
              />
            </div>
            <div className="target">
              <TargetVisualization 
                ends={formData.ends} 
                practiceRounds={formData.practiceRounds}
                distance="15m" 
              />
            </div>
          </div>
        </div>

        {/* Score Summary */}
        <div className="section">
          <h2 className="section-title">Score Summary</h2>
          <div className="score-summary">
            <div className="stats-container">
              <div className="stat-column">
                <p>Total Score: {scores.totalScore}</p>
                <p>10m Score: {scores.tenMeterScore}</p>
                <p>15m Score: {scores.fifteenMeterScore}</p>
                <p>Drop Percentage: {scores.dropPercentage}</p>
              </div>
              <div className="chart-column">
                <h3>End-by-End Progress</h3>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={scores.chartData.length > 0 ? scores.chartData : scores.endAverages.map((score, index) => ({
                        name: `End ${index + 1}`,
                        score
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, 50]} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#8884d8"
                        name="End Score"
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 10 Meter Ends */}
        <div className="section">
          <h2 className="section-title">10 Meter Ends (1-3)</h2>
          <div className="practice-container">
            <PracticeRound
              distance="10m"
              arrows={formData.practiceRounds.tenMeter.arrows}
              onChange={handlePracticeArrowChange}
            />
          </div>
          <div className="ends-container">
            {[1, 2, 3].map(endNum => (
              <End
                key={`end-${endNum}`}
                endNumber={endNum}
                arrows={formData.ends[endNum - 1]?.arrows || Array(5).fill(0)}
                onChange={handleArrowChange}
              />
            ))}
          </div>
        </div>

        {/* 15 Meter Ends */}
        <div className="section">
          <h2 className="section-title">15 Meter Ends (4-6)</h2>
          <div className="practice-container">
            <PracticeRound
              distance="15m"
              arrows={formData.practiceRounds.fifteenMeter.arrows}
              onChange={handlePracticeArrowChange}
            />
          </div>
          <div className="ends-container">
            {[4, 5, 6].map(endNum => (
              <End
                key={`end-${endNum}`}
                endNumber={endNum}
                arrows={formData.ends[endNum - 1]?.arrows || Array(5).fill(0)}
                onChange={handleArrowChange}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="scorecard-footer">
        <div>Acknowledgements: Daniel Filer, Jim Deline</div>
        <div className="dbd-footer">
          <span>Made by DesignByDialogue.ai</span>
          <img 
            src={DBDBottle} 
            alt="DesignByDialogue Logo" 
            className="dbd-logo" 
          />
        </div>
      </div>
    </div>
  );
};

export default ArcheryScorecard;